import { useCallback, useMemo } from 'react';

import { usePaywallContext } from 'components/Paywall';

import { TP } from 'constants/index';
import {
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_PRODUCT_IDS,
  SUBSCRIPTIONS_STATUSES,
  SUBSCRIPTION_PAYMENT_STATUSES,
} from 'constants/subscriptions';

import { createDate } from 'utils/date';

import { ENTITY_TYPES } from 'operabase-router/constants';

import { useProfileAccesses, useUserData } from './useAuthenticatedUser';

const useEntitySubscriptionActions = ({ entity, entityType }) => {
  const user = useUserData();
  const { data: profileAccesses } = useProfileAccesses();
  const { setShowSubscriptionPlans } = usePaywallContext();
  const { targetEntity, targetEntityType } = useMemo(() => {
    if (!entityType || !entity?.id) {
      if (profileAccesses?.data?.length > 0) {
        const matchedOrganization = profileAccesses?.data?.find(access => !!access.organization?.id)?.organization;

        if (matchedOrganization) {
          return {
            targetEntity: matchedOrganization,
            targetEntityType: ENTITY_TYPES.ORGANIZATION,
          };
        }

        const matchedProfile = profileAccesses?.data?.find(access => !!access.profile?.id)?.profile;

        if (matchedProfile) {
          return {
            targetEntity: matchedProfile,
            targetEntityType: ENTITY_TYPES.PROFILE,
          };
        }
      }

      return {
        targetEntity: null,
        targetEntityType: null,
      };
    }

    let matchedEntity = null;

    if (entityType === ENTITY_TYPES.ORGANIZATION) {
      matchedEntity = profileAccesses?.data?.find(access => access.organization?.id === entity?.id)?.organization;
    } else if (ENTITY_TYPES.PROFILE === entityType) {
      matchedEntity = profileAccesses?.data?.find(access => access.profile?.id === entity?.id)?.profile;
    } else {
      matchedEntity = profileAccesses?.data?.find(access => access.user?.id === entity?.id)?.user;
    }

    if (matchedEntity) {
      return {
        targetEntity: matchedEntity,
        targetEntityType: entityType,
      };
    }

    const entitySubscriptions =
      entity?.subscriptions?.filter(
        subscription =>
          [SUBSCRIPTIONS_STATUSES.active, SUBSCRIPTIONS_STATUSES.nonRenewing].includes(subscription?.status) &&
          subscription?.profile?.id === entity?.id,
      ) || [];

    const { activeSubscription, nonRenewingSubscription } = entitySubscriptions.reduce((acc, subscription) => {
      if (subscription?.status === SUBSCRIPTIONS_STATUSES.active) {
        acc.activeSubscription = subscription;
      } else if (subscription?.status === SUBSCRIPTIONS_STATUSES.nonRenewing) {
        acc.nonRenewingSubscription = subscription;
      }

      return acc;
    }, {});

    return {
      targetEntity: {
        ...entity,
        subscription: activeSubscription || nonRenewingSubscription || null,
      },
      targetEntityType: entityType,
    };
  }, [profileAccesses, entityType, entity]);

  const targetSubscription = useMemo(() => {
    const entitySubscription = targetEntity?.subscription;

    if (
      ENTITY_TYPES.PROFILE === entityType &&
      entitySubscription?.user?.id &&
      entitySubscription?.user?.id !== user?.id
    ) {
      return null;
    }

    return entitySubscription;
  }, [targetEntity?.subscription, entityType, user?.id]);

  const onClickHandler = useCallback(
    () =>
      setShowSubscriptionPlans({
        show: true,
        entity: targetEntity,
        entityType: targetEntityType,
        subscription: targetSubscription,
      }),
    [targetEntity, targetEntityType, targetSubscription, setShowSubscriptionPlans],
  );

  const targetSubscriptionPlan = useMemo(() => {
    if (!targetSubscription?.product?.id) {
      return null;
    }

    return Object.values(SUBSCRIPTION_PLANS)
      .flat()
      .find(({ productId }) => productId === targetSubscription?.product?.id);
  }, [targetSubscription?.product?.id]);

  if (!targetEntity || !targetEntityType) {
    return {
      hasAction: false,
      actionLabel: '',
      onClick: () => null,
    };
  }

  let label = `${TP}.AS_SUBSCRIPTIONS_UPGRADE`;
  let canUpgrade = targetSubscriptionPlan?.canUpgrade;

  if (!targetSubscription || targetSubscription?.invoice?.status !== SUBSCRIPTION_PAYMENT_STATUSES.PAID) {
    canUpgrade = true;
  }

  if (ENTITY_TYPES.PROFILE === targetEntityType && targetSubscriptionPlan) {
    const isMonthlyPlan = targetSubscriptionPlan.productId === SUBSCRIPTION_PRODUCT_IDS.PROFILE_MONTHLY;

    if (isMonthlyPlan) {
      label = `${TP}.BTN_UPGRADE_SUBSCRIPTION`;
    }
  }

  const invoiceDueDate = targetSubscription?.invoice?.due_date;
  const invoiceStatus = targetSubscription?.invoice?.status;
  const isDue = invoiceDueDate && createDate(invoiceDueDate * 1000).isSameOrAfter(createDate(), 'minute');
  const isOverdue = invoiceDueDate && createDate(invoiceDueDate * 1000).isBefore(createDate(), 'minute');
  const isUnpaid = invoiceStatus !== SUBSCRIPTION_PAYMENT_STATUSES.PAID;
  const isPaymentDue = isUnpaid && isDue;
  const isPaymentOverdue = isUnpaid && isOverdue;

  if (isPaymentDue) {
    label = `${TP}.FN_PAYMENT_DUE`;

    if (isPaymentOverdue) {
      label = `FN_PAYMENT_OVER_DUE`;
    }
  }

  return {
    hasAction: canUpgrade || isPaymentDue,
    actionLabel: label,
    onClick: onClickHandler,
  };
};

export default useEntitySubscriptionActions;
